import "@westpierstudio/luma";
import "@westpierstudio/core-module-oryx";
import * as PIXI from "pixi.js";
import { ApplicationManager } from "@westpierstudio/luma/application";
import { AudioManager } from "@westpierstudio/luma/audio";
import { PixiFactory } from "@westpierstudio/luma/factory";
import { DefaultLayout } from "@westpierstudio/luma/layout";
import { ResourcesManager } from "@westpierstudio/luma/resources";
import { EventEmitter } from "@westpierstudio/luma/events";
import { constants } from "@westpierstudio/luma/utils";
import { KSGLoadingScreen } from "@westpierstudio/core-module-oryx/game/KSGLoadingScreen";
import { LuckyWealthCatSplashScreen } from "game/LuckyWealthCatSplashScreen";
import { LuckyWealthCatGame } from "game/LuckyWealthCatGame";
import { LuckyWealthCatGameClient } from "game/LuckyWealthCatGameClient";
import { LuckyWealthCatGameController } from "game/LuckyWealthCatGameController";
import { LuckyWealthCatGameModel } from "game/LuckyWealthCatGameModel";
import { LuckyWealthCatGameView } from "game/LuckyWealthCatGameView";
import { GameServices } from "game/GameServices";
import { Comms, CurrencyService, Gamebridge, RGSData, UILayout, UIMessages, Analytics, DebugService, PaytableContentFactoryService, PlayerInactivityService } from "@westpierstudio/core-module-oryx/services";
import { WebSocketDiscoveryProtocol } from "@westpierstudio/core-module-oryx/services/comms";
import { createLocalStorageService } from "@westpierstudio/core-module-oryx/services/localstorage";
import { utils } from "pixi.js";
import { TextureScaleStrategy } from "@westpierstudio/core-module-oryx/services/layout/TextureScaleStrategy";
import { LuckyWealthCatLoading } from "game/LuckyWealthCatLoading";
import { iosNative } from "@westpierstudio/core-module-oryx/utils";
import { LuckyWealthCatContentFactoryService } from "game/paytable/LuckyWealthCatPaytableContentFactoryService";

const development = process.env.NODE_ENV !== `production`;

if (!development) {
    utils.skipHello();
    if (iosNative) {
        console.log(`ios-native`);
    }
} else {
    console.log(`### Build Time: ${constants.buildTime} ###`);
    console.log(`### Use Consolidated Json: ${constants.consolidateJson} ###`);
    console.log(`### Core Version: ${constants.coreVersion} ###`);
    console.log(`### Game Version: ${constants.gameVersion} ###`);
    console.log(`### Core Pixi versions ###`);
    Object.entries(constants.corePixiVersions).forEach(([dependency, version]) => console.log(dependency, version));
    console.log(`#########`);
}

const rgsData = new RGSData({
    gameCode: development ? `BGS_KSG_LUCKY_WEALTHY_CAT_LINES_30_95` : undefined
});

const textureScaleStrategy = new TextureScaleStrategy(iosNative ? [4] : [4]);
textureScaleStrategy.debug = development;

const resources = new ResourcesManager();
const application = new ApplicationManager();
const layout = new DefaultLayout(application, textureScaleStrategy);
const loadScreenLayout = new DefaultLayout(application, textureScaleStrategy);
const splashScreenLayout = new DefaultLayout(application, textureScaleStrategy);
const uiLayout = new UILayout(application, textureScaleStrategy);
const factory = new PixiFactory(resources);
const audio = new AudioManager(application);
const loader = new LuckyWealthCatLoading(resources, layout, audio, rgsData);

const webSocketDiscoveryProtocol = new WebSocketDiscoveryProtocol(rgsData.gameServerURLs);
const adminWebSocketDiscoveryProtocol = new WebSocketDiscoveryProtocol(rgsData.gameServerURLs.map(url => {
    return url.replace(`23011`, `23012`).replace(`game_ws`, `admin_ws`);
}));

const debug = new DebugService();
const comms = new Comms(rgsData, webSocketDiscoveryProtocol);
const adminComms = new Comms(rgsData, adminWebSocketDiscoveryProtocol);
const gameBridge = new Gamebridge();
const gameLocalStorage = createLocalStorageService(`ts-lucky-wealth-cat`, `westpier-studio`);
const currency = new CurrencyService(rgsData.playMode, rgsData.languageCode, debug);
const analytics = new Analytics(comms, rgsData, layout, application);
const uiMessages = new UIMessages(resources);
const playerInactivity = new PlayerInactivityService(debug, rgsData);
const paytableContentFactory = new LuckyWealthCatContentFactoryService();

comms.enableLogging = development;
adminComms.enableLogging = development;
gameBridge.enableLogging = development;

// analytics.debug.useNetwork = false; // uncomment line to prevent network comms analytics spam.
analytics.debug.logging = development;
analytics.debug.unpackContent = development;

const services = new GameServices(
    layout,
    resources,
    application,
    factory,
    loader,
    audio,
    rgsData,
    comms,
    adminComms,
    gameBridge,
    uiLayout,
    gameLocalStorage,
    currency,
    loadScreenLayout,
    splashScreenLayout,
    analytics,
    uiMessages,
    debug,
    playerInactivity,
    paytableContentFactory
);

const gameView = new LuckyWealthCatGameView(services);
const gameModel = new LuckyWealthCatGameModel(services);
const gameClient = new LuckyWealthCatGameClient(services);
const gameEmitter = new EventEmitter();

const gameController = new LuckyWealthCatGameController(
    services,
    gameView,
    gameModel,
    gameEmitter,
    gameClient
);

const loadingScreen = new KSGLoadingScreen(services);
const splashScreen = new LuckyWealthCatSplashScreen(services);

const game = new LuckyWealthCatGame(
    services,
    gameModel,
    gameView,
    gameController,
    loadingScreen,
    splashScreen,
    gameClient
);

game.start();

declare global {
    interface Window {
        game: LuckyWealthCatGame;
        services: GameServices;
        PIXI: typeof PIXI;
    }
}

window.game = game;
window.services = services;
window.PIXI = PIXI;
